import { LocalizedCurrencySimpleValue, SimpleAmount } from '@mews-distributor/common';
import { ProductChargingMode, TimeUnit, UnicodeEntity } from '@mews/enums';
import { LocalizedText, matchTimeUnitKey } from '@mews/framework/localization';
import { Stack, StackItem } from '@optimus-web/core';

interface WithQuantity {
    quantity: number;
}

interface WithUnitPrice {
    unitPrice: SimpleAmount;
}

interface QuantityPerAgeCategory {
    ageCategoryId: string;
    ageCategoryLocalizedName: string;
    quantity: number;
    unitPrice: SimpleAmount;
    personCount: number;
    isDayService: boolean;
    timeUnit: TimeUnit;
}

interface WithQuantityPerAgeCategories {
    quantityPerAgeCategory: QuantityPerAgeCategory[];
}

export interface ProductInformationTextProps extends WithQuantity, WithUnitPrice {
    chargingMode: ProductChargingMode | undefined;
    quantityPerAgeCategory: QuantityPerAgeCategory[];
}

const PerTimeUnitText = ({ quantity, unitPrice }: WithUnitPrice & WithQuantity) => (
    <>
        {`${quantity} ${UnicodeEntity.MultiplicationSign} `}
        <LocalizedCurrencySimpleValue value={unitPrice} />
    </>
);

const MultipliedByTimeUnitText = ({
    quantity,
    isDayService,
    timeUnit,
}: {
    quantity: number;
    isDayService: boolean;
    timeUnit: TimeUnit;
}) => {
    const timeUnitLabelKey = matchTimeUnitKey({
        isServiceDayUse: isDayService,
        timeUnit,
        plural: true,
    });

    if (timeUnitLabelKey == null) {
        return null;
    }

    return (
        <>
            {UnicodeEntity.NoBreakSpace}
            {UnicodeEntity.MultiplicationSign}
            {UnicodeEntity.NoBreakSpace}
            <LocalizedText textKey={timeUnitLabelKey} />
            {UnicodeEntity.Colon}
            {UnicodeEntity.NoBreakSpace}
            {quantity}
        </>
    );
};

const PerPersonPerTimeUnitTextNew = ({ quantityPerAgeCategory }: WithQuantityPerAgeCategories) => (
    <Stack vertical>
        {quantityPerAgeCategory.map(
            ({ unitPrice, personCount, quantity, ageCategoryLocalizedName, isDayService, timeUnit, ageCategoryId }) => (
                <StackItem key={ageCategoryId}>
                    {ageCategoryLocalizedName}
                    {UnicodeEntity.Colon}
                    {UnicodeEntity.NoBreakSpace}
                    {personCount}
                    {UnicodeEntity.NoBreakSpace}
                    {UnicodeEntity.MultiplicationSign}
                    {UnicodeEntity.NoBreakSpace}
                    <LocalizedCurrencySimpleValue value={unitPrice} />
                    {quantity > 1 ? (
                        <MultipliedByTimeUnitText quantity={quantity} isDayService={isDayService} timeUnit={timeUnit} />
                    ) : null}
                </StackItem>
            )
        )}
    </Stack>
);

export const ProductInformationText = ({
    chargingMode,
    quantity,
    quantityPerAgeCategory,
    unitPrice,
}: ProductInformationTextProps) => {
    if (chargingMode == null) {
        return null;
    }

    switch (chargingMode) {
        case ProductChargingMode.PerTimeUnit:
        case ProductChargingMode.Once:
            return <PerTimeUnitText quantity={quantity} unitPrice={unitPrice} />;
        case ProductChargingMode.PerPersonPerTimeUnit:
            return <PerPersonPerTimeUnitTextNew quantityPerAgeCategory={quantityPerAgeCategory} />;
        case ProductChargingMode.PerPerson:
            return <PerPersonPerTimeUnitTextNew quantityPerAgeCategory={quantityPerAgeCategory} />;
        default:
            return null;
    }
};
